import './index.css'
import { Space, Table } from 'antd'
import { HeaderActionButtons } from './components/HeaderActionButtons'
import { CIMSTableProps } from './types'
import CIMSTableProvider, { useCIMSTableContext } from './CIMSTableContext'
import { PropsWithChildren, useState } from 'react'

function InnerCIMSTable<RecordType extends object, InitialValues = any>({
  children,
  clickable,
  pagination,
  size,
  details,
}: React.PropsWithChildren<
  Pick<
    CIMSTableProps<RecordType, InitialValues>,
    'expand' | 'pagination' | 'size' | 'details'
  > & {
    clickable: (record: RecordType) => boolean
  }
>) {
  const { columns, dataSource, loading, expandable, rowKey, actions } =
    useCIMSTableContext<RecordType>()
  const { collapse, deepLink, create, csv, custom, onReload, payItems } =
    actions.header
  /**
   * we need to get the current data source's length to correctly handle the
   * column/column width problem
   */
  const [length, setLength] = useState(dataSource.length)
  /**
   * for some reason, antd is isn't getting rid of 10 as one of the default page
   * sizes, EVEN THOUGH WE'RE SETTING A CUSTOM ARRAY OF PAGE SIZE VALUES!!
   *
   * 10, 25, 50 and 100 are available on page load, but then filter down to
   * what's expected immediately after selecting any other size
   *
   * so it would appear we have to manually set the page size to get the damn
   * thing to work as expected
   */
  const [pageSize, setPageSize] = useState(25)
  return (
    <Space
      direction="vertical"
      size="large"
      style={{
        /**
         * need width here to prevent some things...
         * - setting a scrollbar on the entire window
         * - growing unconstrained after render
         *
         * you may need to set a maxWidth on a CIMSTable if it is nested in the
         * expandable section of another CIMSTable however
         */
        width: '100%',
      }}
    >
      {details}
      {(collapse.visible ||
        !!create ||
        !!csv ||
        !!custom ||
        !!deepLink.value ||
        !!onReload ||
        !!payItems) && <HeaderActionButtons />}
      <Table
        onChange={(_pagination, _filters, _sorter, { currentDataSource }) =>
          setLength(currentDataSource.length)
        }
        /**
         * have to use a fixed table to get fixed columns to work as expected
         */
        tableLayout="fixed"
        size={size}
        /**
         * if the row is expandable, we want to set the cursor to pointer for
         * the row
         *
         * CIMSTableCell will override this behavior by resetting the cursor back
         * to 'auto' leaving a situation where the container is "clickable", but
         * the content isn't, unless specified
         */
        onRow={(record) => {
          if (clickable(record)) return { style: { cursor: 'pointer' } }
          return {}
        }}
        scroll={{
          /**
           * have to set 'max-content' in order to force the fixed layout with
           * fixed column headers to render correctly
           *
           * however, if you set 'max-content' with no data, the headers will
           * get all messed up...
           */
          x: length ? 'max-content' : undefined,
          /**
           * must set a height to prevent the table from creating scroll bars on
           * the entire window
           *
           * unfortunately, this will force vertical scrollbars to be rendered
           * on single record tables. We can't override this behavior with
           * 'overflow: auto' in our styles because Ant adds a dummy column for
           * the scrollbar
           */
          y: '60vh',
        }}
        pagination={{
          ...pagination,
          position: ['bottomRight'],
          pageSizeOptions: [25, 50, 100],
          hideOnSinglePage: true,
          pageSize,
          onChange: (_page, pageSize) => setPageSize(pageSize),
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, [from, to]) => (
            <div style={{ marginRight: '8px' }}>
              {from}-{to} of {total}
            </div>
          ),
        }}
        loading={loading}
        dataSource={dataSource}
        /**
         * does not like keys of type...
         *
         * but rowKey is a subset of keyof T, so we'll leave it
         */
        // @ts-expect-error
        rowKey={rowKey}
        columns={columns}
        expandable={expandable}
      />
      {children}
    </Space>
  )
}

/**
 * the {@link CIMSTable} is the "end-all-be-all" table element for the CIMS application
 *
 * this should be your go-to table component
 *
 * it includes, or will include, all the configuration we need to build out
 * common table views throughout the application
 *
 * you have the ability to configure actions, custom fields, an expandable section,
 * a pay item selector, along with type-safe and simple filter and sort options
 *
 * ## Additional Configuration
 *
 * wrap any CIMSTable components in the ProjectCustomFieldsProvider if you want to use
 * custom fields
 *
 * wrap any CIMSTable components in the PayItemSelectProvider if you want to use
 * pay items
 *
 * use {@link useCIMSTableContext} to get the context associated with the component.
 * this allows you to toggle the simple edit button and get computed values for various
 * properties associated with the {@link CIMSTable}
 *
 * ## Known Issues & Workarounds
 *
 * ### Grow after render
 *
 * sometimes, when placed in the expandable section of another CIMSTable, no
 * matter what you do, the table will grow after render...
 *
 * wrap it in a div, potentially with it's own maxWidth, to deal with it
 *
 * ### Show All button available for every sub-table
 *
 * Because we're using the context API, every CIMS table will have access to the
 * values of its nearest Context. This can lead to situations where a deep-linkable
 * table will enable the Show All button for every CIMS table found in its expandable
 * section (recursively)
 *
 * use {@link CIMSTableProps.disable} to deal with those
 *
 * @param props {@link CIMSTableProps}
 * @see {@link useProjectCustomFields}
 * @see {@link InnerCIMSTable}
 * @see {@link CIMSTableProvider}
 * @see {@link useCIMSTableContext}
 */
function CIMSTable<RecordType extends object, InitialValues = any>({
  children,
  pagination,
  size,
  details,
  ...rest
}: PropsWithChildren<CIMSTableProps<RecordType, InitialValues>>) {
  const clickable: (record: RecordType) => boolean =
    typeof rest.expand?.expandable === 'function'
      ? rest.expand.expandable
      : () => !!rest.expand
  const props = { pagination, clickable, size, details }
  return (
    <CIMSTableProvider {...rest}>
      <InnerCIMSTable {...props}>{children}</InnerCIMSTable>
    </CIMSTableProvider>
  )
}

export default CIMSTable
